import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Callout, Card, Elevation, Intent, Spinner, Tag, Tooltip } from '@blueprintjs/core';
import ToastStore from '../../stores/ToastStore';

const COPY_FEEDBACK_MS = 2000;

const CredentialCell = ({
    value,
    label,
    copyKey,
    copiedKey,
    onCopy,
    isSecret = false,
    isVisible = false,
    onToggleVisibility,
}) => {

    const maskSecret = secret => {
        if (!secret) return '';
        const lastFour = secret.slice(-4);
        return `${'•'.repeat(Math.max(secret.length - 4, 8))}${lastFour}`;
    };

    const displayValue =
        isSecret && !isVisible
            ? maskSecret(value)
            : value;

    const isCopied = copiedKey === copyKey;

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
            }}
        >
            <span
                style={{
                    fontFamily: 'monospace',
                    fontSize: 13,
                    wordBreak: 'break-all',
                }}
            >
                {displayValue}
            </span>

            <Tooltip portalContainer={document.body} content={I18n.t('js.copy_to_clipboard')}>
                <span
                    role="button"
                    tabIndex={0}
                    aria-label={I18n.t('js.copy_to_clipboard')}
                    className={`bp3-icon ${isCopied
                        ? 'bp3-icon-tick'
                        : 'bp3-icon-duplicate'
                        } bp3-cursor-pointer`}
                    style={{
                        color: '#137cbd',
                        fontSize: '18px',
                        flexShrink: 0,
                    }}
                    onClick={() => onCopy(value, label, copyKey)}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            onCopy(value, label, copyKey);
                        }
                    }}
                />
            </Tooltip>

            {isSecret && onToggleVisibility && (
                <Tooltip portalContainer={document.body} content={isVisible ? 'Hide secret' : 'Show secret'}>
                    <span
                        role="button"
                        tabIndex={0}
                        className={`bp3-icon ${isVisible
                            ? 'bp3-icon-eye-open'
                            : 'bp3-icon-eye-off'
                            } bp3-cursor-pointer`}
                        style={{
                            color: '#5c7080',
                            fontSize: '18px',
                            flexShrink: 0,
                        }}
                        onClick={onToggleVisibility}
                    />
                </Tooltip>
            )}
        </div>
    );
};

const AccountSettingsApiCredentials = () => {
    const [clients, setClients] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [visibleSecrets, setVisibleSecrets] = useState({});
    const [copiedKey, setCopiedKey] = useState(null);

    const apiBase = Cookies.get('apiEnv');

    const copyText = (text, label, copyKey) => {
        if (!text) {
            return;
        }

        const onSuccess = () => {
            ToastStore.showToast(`${label} copied to clipboard`, 'success');
            setCopiedKey(copyKey);
            setTimeout(() => setCopiedKey(null), COPY_FEEDBACK_MS);
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(onSuccess);
        } else {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            onSuccess();
        }
    };

    const toggleSecretVisibility = key => {
        setVisibleSecrets(prev => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const fetchClients = () => {
        setFetching(true);
        return axios
            .get(`${apiBase}/integration_clients`)
            .then(response => {
                if (Array.isArray(response.data)) {
                    setClients(response.data);
                } else if (response.data && typeof response.data === 'object' && Object.keys(response.data).length > 0) {
                    setClients([response.data]);
                } else {
                    setClients([]);
                }
            })
            .catch(error => {
                const errors = error?.response?.data?.error || error?.response?.data?.errors || [error.message];
                const message = Array.isArray(errors) ? errors.join(', ') : String(errors);
                ToastStore.showToast(message, 'danger');
            })
            .finally(() => {
                setFetching(false);
            });
    };

    useEffect(() => {
        fetchClients();
    }, []);

    return (
        <div className="account-settings-api-credentials">
            <h2>API Credentials</h2>
            <div>
                {fetching ? (
                    <div style={{ padding: '24px 0' }}>
                        <Spinner />
                    </div>
                ) : clients.length === 0 ? (
                    <Callout intent={Intent.WARNING} style={{ marginTop: '16px' }}>
                        No API credentials found.
                    </Callout>
                ) : (
                    <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {clients.map((client) => (
                            <div key={client.id}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#5C7080', letterSpacing: '0.5px' }}>{I18n.t('js.client_id')}</div>
                                        <div style={{ width: '50%', padding: '8px 12px', backgroundColor: '#F5F8FA', borderRadius: '4px', border: '1px solid #E1E8ED' }}>
                                            <CredentialCell
                                                value={client.client_id}
                                                label={I18n.t('js.client_id')}
                                                copyKey={`${client.id}-client-id`}
                                                copiedKey={copiedKey}
                                                onCopy={copyText}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#5C7080', letterSpacing: '0.5px' }}>{I18n.t('js.client_secret')}</div>
                                        <div style={{ width: '50%', padding: '8px 12px', backgroundColor: '#F5F8FA', borderRadius: '4px', border: '1px solid #E1E8ED' }}>
                                            <CredentialCell
                                                value={client.client_secret}
                                                label={I18n.t('js.client_secret')}
                                                copyKey={`${client.id}-client-secret`}
                                                copiedKey={copiedKey}
                                                onCopy={copyText}
                                                isSecret
                                                isVisible={!!visibleSecrets[`${client.id}-client-secret`]}
                                                onToggleVisibility={() => toggleSecretVisibility(`${client.id}-client-secret`)}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#5C7080', letterSpacing: '0.5px' }}>API Key</div>
                                        <div style={{ width: '50%', padding: '8px 12px', backgroundColor: '#F5F8FA', borderRadius: '4px', border: '1px solid #E1E8ED' }}>
                                            <CredentialCell
                                                value={client.api_key}
                                                label="API Key"
                                                copyKey={`${client.id}-api-key`}
                                                copiedKey={copiedKey}
                                                onCopy={copyText}
                                                isSecret
                                                isVisible={!!visibleSecrets[`${client.id}-api-key`]}
                                                onToggleVisibility={() => toggleSecretVisibility(`${client.id}-api-key`)}
                                            />
                                        </div> 
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AccountSettingsApiCredentials;
